import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { useState, type ReactNode } from "react";
import { paneIsCompact } from "work/_views/work-pane-layout";
import { ProposalActionButton } from "work/proposals/_views/_shared/proposal-styled-components";

/** The dropdown's name, since it has no text of its own. Exported for the specs and the browser harness. */
export const MORE_WAYS_TO_SUBMIT_LABEL = "More ways to submit";

/**
 * Another way to submit, behind the arrow beside Submit: Send to Several Firms (#6455), or handing the
 * draft over to the team leader who will submit it (#6579).
 */
export type ProposalSubmitOption = {
  key: string;
  label: string;
  /** A second line under the label, saying who the option reaches. */
  description?: string;
  icon: ReactNode;
  disabled?: boolean;
  onClick: () => void;
};

// The group stretches the tooltip's span to the arrow's height; a flex span passes that on to Submit,
// which otherwise stands a pixel short of the arrow. On a compact pane the group fills its grid cell,
// as Submit alone does, with the arrow kept to its own width.
const SubmitGroup = styled(ButtonGroup)(({ theme }) => ({
  "& > span:first-of-type": {
    display: "flex",
  },
  [paneIsCompact(theme)]: {
    display: "flex",
    width: "100%",
    "& > span:first-of-type": {
      flex: "1 1 auto",
      minWidth: 0,
    },
  },
}));

const SubmitArrow = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(5),
  paddingLeft: 0,
  paddingRight: 0,
}));

/**
 * The draft page's Submit, with the other ways to submit behind an arrow beside it (#6455).
 *
 * Submit keeps its own tooltip, which explains why it is disabled; the arrow is disabled only when
 * every option behind it is. With no options the arrow is left out and this is the plain Submit.
 */
export default function ProposalSubmitSplitButton(
  props: Readonly<{
    compact: boolean;
    disabled: boolean;
    loading: boolean;
    /** Why Submit is disabled, or what it will do. Shown on Submit alone, not on the arrow. */
    tooltip: ReactNode;
    startIcon: ReactNode;
    options: readonly ProposalSubmitOption[];
    onSubmit: () => void;
  }>,
) {
  const { compact, disabled, loading, tooltip, startIcon, options, onSubmit } = props;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const size = compact ? "small" : "medium";

  const submit = (
    <Tooltip enterDelay={600} placement="bottom" title={tooltip}>
      {/* A span keeps the tooltip reachable while Submit is disabled. */}
      <span>
        {/* Filled: submitting is what a draft is for, beside the outlined actions that manage it (#5973). */}
        <ProposalActionButton
          variant="contained"
          color="success"
          size={size}
          startIcon={startIcon}
          loading={loading}
          disabled={disabled}
          onClick={onSubmit}
        >
          Submit
        </ProposalActionButton>
      </span>
    </Tooltip>
  );
  if (options.length === 0) return submit;

  return (
    <>
      <SubmitGroup variant="contained" color="success" size={size} disableElevation>
        {submit}
        <SubmitArrow
          aria-label={MORE_WAYS_TO_SUBMIT_LABEL}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          disabled={options.every((option) => option.disabled)}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        >
          <ArrowDropDownIcon />
        </SubmitArrow>
      </SubmitGroup>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.key}
            disabled={option.disabled}
            onClick={() => {
              setMenuAnchor(null);
              option.onClick();
            }}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText primary={option.label} secondary={option.description} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
